// src/components/Supplier/SupplierModal.jsx
// Modal component for adding/editing suppliers

import React, { useState, useEffect } from 'react';

/**
 * SupplierModal Component
 * A modal dialog for creating or editing suppliers
 * 
 * @param {boolean} isOpen - Controls whether modal is visible
 * @param {Function} onClose - Callback when modal should close
 * @param {Function} onSave - Callback when form is submitted
 * @param {Object} supplier - Existing supplier data (for edit mode)
 * @param {boolean} isSaving - Whether save operation is in progress
 */
const SupplierModal = ({ isOpen, onClose, onSave, supplier, isSaving }) => {

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    address: '',
    isActive: true
  });

  // Populate form when editing
  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name || '',
        contact: supplier.contact || '',
        address: supplier.address || '',
       isActive: supplier.isActive ?? true   // ✅ default true
      });
    } else {
      setFormData({
        name: '',
        contact: '',
        address: '',
        isActive: formData.isActive
      });
    }
  }, [supplier]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Please enter a supplier name');
      return;
    }

    // ✅ SEND SINGLE OBJECT ONLY
    onSave({
      name: formData.name,
      contact: formData.contact,
      address: formData.address,
      isActive: formData.isActive
    });

    // ✅ CLEAR FORM AFTER SUCCESS
    setFormData({
      name: '',
      contact: '',
      address: '',
      isActive: true
    });
  };

  // Handle modal close
  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="inventory-modal-overlay">
      <div className="inventory-modal-content">
        {/* Modal Header */}
        <header className="inventory-modal-header">
          <h3 className="inventory-modal-title">
            {supplier ? `Edit Supplier: ${supplier.name}` : 'Add New Supplier'}
          </h3>
          <button
            className="inventory-close-modal-btn"
            onClick={handleClose}
            disabled={isSaving}
            type="button"
          >
            ✕
          </button>
        </header>

        {/* Modal Body */}
        <div className="inventory-modal-body">
          <form id="supplierForm" onSubmit={handleSubmit}>
            <div className="inventory-form-grid">
              
              {/* Supplier Name */}
              <label htmlFor="name">
                Supplier Name <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className="inventory-custom-input"
                placeholder="e.g., Mr Kumara"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={isSaving}
              />

              {/* Contact */}
              <label htmlFor="contact">Contact</label>
              <input
                type="text"
                id="contact"
                name="contact"
                className="inventory-custom-input"
                placeholder="e.g., 07-xxxxxxx"
                value={formData.contact}
                onChange={handleChange}
                disabled={isSaving}
              />

              {/* Address */}
              <label htmlFor="address">Address</label>
              <input
                type="text"
                id="address"
                name="address"
                className="inventory-custom-input"
                placeholder="e.g., 123, Hunupitiya, Wattala"
                value={formData.address}
                onChange={handleChange}
                disabled={isSaving}
              />

              {/* Active */}
              <label htmlFor="isActive">Active</label>
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={handleCheckboxChange}
                disabled={isSaving}
              />
            </div>
          </form>
        </div>

        {/* Modal Footer */}
        <footer className="inventory-modal-footer">
          <button
            className="inventory-btn inventory-btn-cancel"
            onClick={handleClose}
            disabled={isSaving}
            type="button"
          >
            Cancel
          </button>
          <button
            className="inventory-btn inventory-btn-save"
            disabled={isSaving}
            type="submit"
            form="supplierForm"
          >
            {isSaving ? 'Saving...' : 'Save Supplier'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default SupplierModal;
